import { cn } from '@sohanemon/utils';
import { useEffectOnce } from '@sohanemon/utils/hooks';
import { m } from '@sohanscript/i18n';
import {
	animate,
	type MotionValue,
	motion,
	useDragControls,
	useMotionValue,
} from 'motion/react';
import {
	type ComponentProps,
	type MouseEvent as ReactMouseEvent,
	type ReactNode,
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Hitbox } from './hitbox';
import {
	SurfaceContext,
	type SurfaceContextValue,
	type SurfaceDrag,
} from './surface-context';
import {
	clampPosition,
	dragRange,
	initialPosition,
	type SurfacePlacement,
} from './surface-geometry';

export type { SurfacePlacement };

/**
 * The shared frame every floating surface in the product is built on:
 * the dialog popup, the inspector window and the `/os` desktop windows.
 *
 * One frame owns the skin, the drag mechanics, the bounds clamp and the
 * chrome row, so a change to any of those lands in every place at once.
 */

/** The skin of the frame. */
export type SurfaceVariant =
	/** Modal surface: centered, padded, popover background. */
	| 'modal'
	/** Window surface: draggable desktop window with a chrome row. */
	| 'window';

/** What "maximized" means for a given surface. */
export type SurfaceMaximizedMode =
	/** Fills the whole viewport. */
	| 'cover'
	/** Fills the viewport except the dock gutter. */
	| 'dock';

/**
 * The lifecycle of a surface. Every surface animates with the same
 * vocabulary, so a window appearing and a dialog appearing read the same.
 */
export type SurfaceState = 'open' | 'minimized' | 'closed';

const BOUNDS_PADDING = 10;

/** Shared transition timings, in seconds. */
const SURFACE_TRANSITIONS: Record<
	SurfaceState,
	{ duration: number; ease: 'easeOut' | 'easeIn' }
> = {
	open: { duration: 0.15, ease: 'easeOut' },
	minimized: { duration: 0.2, ease: 'easeOut' },
	closed: { duration: 0.1, ease: 'easeIn' },
};

/** How long a surface stays mounted while it animates out. */
export const SURFACE_EXIT_MS = SURFACE_TRANSITIONS.closed.duration * 1000;

/** How long a surface takes to fold into the dock. */
export const SURFACE_MINIMIZE_MS =
	SURFACE_TRANSITIONS.minimized.duration * 1000;

const SURFACE_ENTER = { opacity: 0, scale: 0.96 };
const SURFACE_OPEN = { opacity: 1, scale: 1 };
const SURFACE_MINIMIZED = { opacity: 0, scale: 0.85 };
const SURFACE_CLOSED = { opacity: 0, scale: 0.95 };

const MODAL_SKIN =
	'grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-xl bg-popover p-4 text-popover-foreground text-sm ring-1 ring-foreground/10 sm:max-w-sm';

const WINDOW_SKIN =
	'flex flex-col overflow-hidden rounded-xl border bg-background text-foreground shadow-2xl';

const CENTERED_GEOMETRY = 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2';

const ORIGIN_GEOMETRY = 'top-0 left-0';

const COVER_GEOMETRY =
	'top-0 left-0 h-dvh w-full translate-x-0 translate-y-0 rounded-none';

const DOCK_GEOMETRY = 'inset-y-0 start-16 end-0 rounded-none';

export type UseSurfaceDragOptions = {
	/** Allows dragging, defaults to true. */
	enabled?: boolean;
	/** Where the surface starts, defaults to `center`. */
	placement?: SurfacePlacement;
	/** Offset applied to the initial position. */
	initialOffset?: { x: number; y: number };
	/** Gap kept between the surface and the viewport edge, defaults to 10px. */
	boundsPadding?: number;
	/** Minimum visible pixels when dragging off-screen on left/right/bottom edges. Defaults to 100px. */
	minVisible?: number;
	/** Frame element, when the caller already owns the ref. */
	frameRef?: RefObject<HTMLDivElement | null>;
};

/**
 * The drag controller behind every surface: motion values, drag controls,
 * the initial placement and the bounds clamp.
 */
export function useSurfaceDrag({
	enabled = true,
	placement = 'center',
	initialOffset,
	boundsPadding = BOUNDS_PADDING,
	minVisible = 100,
	frameRef: providedRef,
}: UseSurfaceDragOptions = {}): SurfaceDrag {
	const internalRef = useRef<HTMLDivElement | null>(null);
	const frameRef = providedRef ?? internalRef;
	const controls = useDragControls();
	const [isDragging, setIsDragging] = useState(false);
	const x = useMotionValue(0);
	const y = useMotionValue(0);
	const offsetRef = useRef(initialOffset);

	const place = useCallback(() => {
		const node = frameRef.current;
		if (!node) return;

		const position = initialPosition({
			placement,
			offset: offsetRef.current ?? { x: 0, y: 0 },
			size: { width: node.offsetWidth, height: node.offsetHeight },
			viewport: { width: window.innerWidth, height: window.innerHeight },
			padding: boundsPadding,
		});

		x.set(position.x);
		y.set(position.y);
	}, [boundsPadding, frameRef, placement, x, y]);

	// NOTE: the first paint has to land on the requested position, so the
	// frame is measured once it is in the DOM.
	useEffectOnce(place);

	const clampToBounds = useCallback(() => {
		const node = frameRef.current;
		if (!node) return;

		const position = clampPosition(
			{ x: x.get(), y: y.get() },
			dragRange({
				placement,
				size: { width: node.offsetWidth, height: node.offsetHeight },
				viewport: {
					width: window.innerWidth,
					height: window.innerHeight,
				},
				padding: boundsPadding,
				minVisible,
			}),
		);

		animate(x, position.x, { type: 'spring', stiffness: 300, damping: 30 });
		animate(y, position.y, { type: 'spring', stiffness: 300, damping: 30 });
	}, [boundsPadding, frameRef, placement, x, y, minVisible]);

	const startDrag = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!enabled) return;
			const target = event.target;
			if (target instanceof HTMLElement && target.closest('button')) return;
			controls.start(event);
		},
		[controls, enabled],
	);

	const handleDragStart = useCallback(() => setIsDragging(true), []);

	const handleDragEnd = useCallback(() => {
		setIsDragging(false);
		clampToBounds();
	}, [clampToBounds]);

	return {
		x,
		y,
		controls,
		frameRef,
		isDragging,
		isEnabled: enabled,
		startDrag,
		clampToBounds,
		resetPosition: place,
		handleDragStart,
		handleDragEnd,
	};
}

export type SurfaceFrameProps = Omit<
	ComponentProps<typeof motion.div>,
	'children' | 'initial' | 'animate' | 'transition'
> & {
	/** The skin of the frame, defaults to `modal`. */
	variant?: SurfaceVariant;
	/** Where the frame sits before it is dragged, defaults to `center`. */
	placement?: SurfacePlacement;
	/** Renders the maximized geometry. */
	maximized?: boolean;
	/** What maximized means, defaults to `cover`. */
	maximizedMode?: SurfaceMaximizedMode;
	/** Lifecycle of the surface, drives the shared transition. */
	state?: SurfaceState;
	/** Focused surfaces get the brighter border and shadow. */
	isFocused?: boolean;
	/** Allows dragging the frame from its chrome. */
	draggable?: boolean;
	/** Drag controller, when the caller needs the motion values itself. */
	dragController?: SurfaceDrag;
	/** Offset applied to the initial position. */
	initialOffset?: { x: number; y: number };
	/** Minimum visible pixels when dragging off-screen on left/right/bottom edges. Defaults to 100px. */
	minVisible?: number;
	children?: ReactNode;
};

/**
 * The frame itself: skin, placement, drag and the context every surface
 * child reads from.
 */
export function SurfaceFrame({
	variant = 'modal',
	placement = 'center',
	maximized = false,
	maximizedMode = 'cover',
	state = 'open',
	isFocused = false,
	draggable = false,
	dragController: providedDrag,
	initialOffset,
	minVisible = 100,
	className,
	children,
	style,
	ref,
	...props
}: SurfaceFrameProps) {
	const internalDrag = useSurfaceDrag({
		enabled: draggable && !maximized,
		placement,
		initialOffset,
		minVisible,
	});
	const drag = providedDrag ?? internalDrag;
	// NOTE: a caller that brings its own controller also brings the decision
	// of whether the surface can be dragged.
	const isDraggable =
		(providedDrag ? providedDrag.isEnabled : draggable) && !maximized;

	const setFrameRef = useCallback(
		(node: HTMLDivElement | null) => {
			drag.frameRef.current = node;
			if (typeof ref === 'function') {
				ref(node);
				return;
			}
			if (ref) ref.current = node;
		},
		[drag.frameRef, ref],
	);

	const geometry = maximized
		? maximizedMode === 'cover'
			? COVER_GEOMETRY
			: DOCK_GEOMETRY
		: placement === 'center'
			? CENTERED_GEOMETRY
			: ORIGIN_GEOMETRY;

	const contextValue = useMemo<SurfaceContextValue>(
		() => ({ drag, maximized, isDraggable, isFocused }),
		[drag, isDraggable, maximized, isFocused],
	);

	return (
		<SurfaceContext.Provider value={contextValue}>
			<motion.div
				{...props}
				ref={setFrameRef}
				initial={SURFACE_ENTER}
				animate={
					state === 'minimized'
						? SURFACE_MINIMIZED
						: state === 'closed'
							? SURFACE_CLOSED
							: SURFACE_OPEN
				}
				transition={SURFACE_TRANSITIONS[state]}
				drag={isDraggable}
				dragControls={drag.controls}
				dragListener={false}
				dragMomentum={false}
				dragElastic={0}
				onDragStart={drag.handleDragStart}
				onDragEnd={drag.handleDragEnd}
				style={{
					...style,
					x: maximized ? 0 : drag.x,
					y: maximized ? 0 : drag.y,
				}}
				className={cn(
					'corner-bevel fixed z-50 outline-none',
					variant === 'modal' ? MODAL_SKIN : WINDOW_SKIN,
					variant === 'window' &&
						(maximized
							? 'border-primary/20 shadow-black/20'
							: isFocused
								? 'border-primary/50 shadow-primary/20'
								: 'border-primary/20 shadow-black/20'),
					className,
					// NOTE: the geometry comes last, the frame owns where the
					// surface sits and how big it is, a caller can add styles
					// but never move or resize the frame.
					geometry,
				)}
			>
				{children}
			</motion.div>
		</SurfaceContext.Provider>
	);
}

const RESIZE_HANDLES = [
	{
		key: 'tl',
		directions: 'nw',
		label: m.window_resize_tl,
		cursor: 'cursor-nwse-resize',
		placement: '-top-1 -left-1 size-4',
	},
	{
		key: 'tr',
		directions: 'ne',
		label: m.window_resize_tr,
		cursor: 'cursor-nesw-resize',
		placement: '-top-1 -right-1 size-4',
	},
	{
		key: 'bl',
		directions: 'sw',
		label: m.window_resize_bl,
		cursor: 'cursor-nesw-resize',
		placement: '-bottom-1 -left-1 size-4',
	},
	{
		key: 'br',
		directions: 'se',
		label: m.window_resize_br,
		cursor: 'cursor-nwse-resize',
		placement: '-bottom-1 -right-1 size-4',
	},
	{
		key: 'top',
		directions: 'n',
		label: m.window_resize_top,
		cursor: 'cursor-ns-resize',
		placement: '-top-1 left-1/2 h-4 w-[calc(100%-24px)] -translate-x-1/2',
	},
	{
		key: 'bottom',
		directions: 's',
		label: m.window_resize_bottom,
		cursor: 'cursor-ns-resize',
		placement: '-bottom-1 left-1/2 h-4 w-[calc(100%-24px)] -translate-x-1/2',
	},
	{
		key: 'left',
		directions: 'w',
		label: m.window_resize_left,
		cursor: 'cursor-ew-resize',
		placement: 'top-1/2 -left-1 h-[calc(100%-24px)] w-4 -translate-y-1/2',
	},
	{
		key: 'right',
		directions: 'e',
		label: m.window_resize_right,
		cursor: 'cursor-ew-resize',
		placement: 'top-1/2 -right-1 h-[calc(100%-24px)] w-4 -translate-y-1/2',
	},
] as const;

export type SurfaceResizeHandlesProps = {
	/** Receives the new size of the surface. */
	onResize: (size: { width: number; height: number }) => void;
	/** Smallest width the surface can be resized to. */
	minWidth: number;
	/** Smallest height the surface can be resized to. */
	minHeight: number;
	/** The surface element, measured while resizing. */
	frameRef: RefObject<HTMLDivElement | null>;
	/** Horizontal position, moved when resizing from the left edge. */
	x: MotionValue<number>;
	/** Vertical position, moved when resizing from the top edge. */
	y: MotionValue<number>;
	/** Turns the handles off, e.g. while the surface is maximized. */
	disabled?: boolean;
	/** Reports the start and the end of a resize gesture. */
	onResizingChange?: (isResizing: boolean) => void;
};

/** Edge and corner handles that resize a surface around its position. */
export function SurfaceResizeHandles({
	onResize,
	minWidth,
	minHeight,
	frameRef,
	x,
	y,
	disabled = false,
	onResizingChange,
}: SurfaceResizeHandlesProps) {
	const startResize = useCallback(
		(event: ReactMouseEvent, directions: string) => {
			event.preventDefault();
			event.stopPropagation();
			if (disabled) return;

			const node = frameRef.current;
			if (!node) return;

			const startX = x.get();
			const startY = y.get();
			const startWidth = node.offsetWidth;
			const startHeight = node.offsetHeight;
			const startClientX = event.clientX;
			const startClientY = event.clientY;

			onResizingChange?.(true);

			const onMove = (moveEvent: MouseEvent) => {
				const deltaX = moveEvent.clientX - startClientX;
				const deltaY = moveEvent.clientY - startClientY;

				let newWidth = startWidth;
				let newHeight = startHeight;
				let newX = startX;
				let newY = startY;

				if (directions.includes('e')) {
					newWidth = Math.max(minWidth, startWidth + deltaX);
				} else if (directions.includes('w')) {
					const proposedWidth = Math.max(minWidth, startWidth - deltaX);
					newX = startX + (startWidth - proposedWidth);
					newWidth = proposedWidth;
				}

				if (directions.includes('s')) {
					newHeight = Math.max(minHeight, startHeight + deltaY);
				} else if (directions.includes('n')) {
					const proposedHeight = Math.max(minHeight, startHeight - deltaY);
					newY = startY + (startHeight - proposedHeight);
					newHeight = proposedHeight;
				}

				x.set(newX);
				y.set(newY);
				onResize({ width: newWidth, height: newHeight });
			};

			const onUp = () => {
				onResizingChange?.(false);
				document.removeEventListener('mousemove', onMove);
				document.removeEventListener('mouseup', onUp);
			};

			document.addEventListener('mousemove', onMove);
			document.addEventListener('mouseup', onUp);
		},
		[disabled, frameRef, minHeight, minWidth, onResize, onResizingChange, x, y],
	);

	if (disabled) return null;

	return (
		<>
			{RESIZE_HANDLES.map((handle) => (
				<Hitbox key={handle.key} size="xs" className="absolute">
					<button
						type="button"
						aria-label={handle.label()}
						className={`absolute z-50 ${handle.placement} ${handle.cursor}`}
						onMouseDown={(event) => startResize(event, handle.directions)}
						style={{ touchAction: 'none' }}
					/>
				</Hitbox>
			))}
		</>
	);
}
